import type { FC } from "react"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  alpha,
  Checkbox,
  Stack,
  Typography,
  useTheme,
} from "@mui/material"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"

import type { CsvSource } from "types/csv.types"
import EntityIcon from "widgets/common/EntityIcon"
import WeatherServiceIcon from "icons/WeatherServiceIcon"
import CsvTelemetryTable from "./CsvTelemetryTable"

interface CsvSourceAccordionProps {
  source: CsvSource
  selected: Set<string>
  expanded: boolean
  onExpandedChange: (expanded: boolean) => void
  onToggleTelemetry: (sourceKey: string, apiName: string) => void
  onToggleAll: (sourceKey: string, checked: boolean) => void
}

const CsvSourceAccordion: FC<CsvSourceAccordionProps> = ({
  source,
  selected,
  expanded,
  onExpandedChange,
  onToggleTelemetry,
  onToggleAll,
}) => {
  const theme = useTheme()
  const total = source.telemetries.length
  const selectedCount = selected.size
  const allChecked = total > 0 && selectedCount === total
  const someChecked = selectedCount > 0 && selectedCount < total

  return (
    <Accordion
      variant="outlined"
      disableGutters
      expanded={expanded}
      onChange={(_, isExpanded) => onExpandedChange(isExpanded)}
      sx={{
        backgroundColor: "transparent",
        borderColor: (theme) => theme.palette.neutral[400],
        borderRadius: 1,
        "&:before": { display: "none" },
        mb: 2,
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{ backgroundColor: (theme) => alpha(theme.palette.background.light, 0.5) }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ width: "100%" }}>
          <Checkbox
            size="small"
            checked={allChecked}
            indeterminate={someChecked}
            disabled={total === 0}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => onToggleAll(source.key, e.target.checked)}
          />
          {source.isWeather ? (
            <WeatherServiceIcon
              cloudColor={theme.palette.text.primary}
              sunColor={theme.palette.text.primary}
              width={22}
              height={18}
            />
          ) : (
            <EntityIcon entity={source.entity} color={theme.palette.text.primary} />
          )}
          <Stack sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: "break-word" }}>
              {source.displayName}
            </Typography>
            {source.subtitle && (
              <Typography
                variant="caption"
                sx={{
                  color: (theme) => theme.palette.text.secondary,
                  wordBreak: "break-word",
                }}
              >
                {source.subtitle}
              </Typography>
            )}
          </Stack>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        <CsvTelemetryTable
          telemetries={source.telemetries}
          selected={selected}
          onToggle={(apiName) => onToggleTelemetry(source.key, apiName)}
        />
      </AccordionDetails>
    </Accordion>
  )
}

export default CsvSourceAccordion
